import * as i0 from '@angular/core';
import { NgModule, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * @deprecated Use functions directly instead injecting via service.
 */
class FileModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: FileModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.7", ngImport: i0, type: FileModule, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: FileModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: FileModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule],
                    exports: [],
                }]
        }] });

const bytesToSize = (bytes, separator = ' ') => {
    const sizes = ['B', 'kB', 'MB', 'GB', 'TB'];
    if (bytes === 0) {
        return 'n/a';
    }
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    if (i === 0) {
        return `${bytes}${separator}${sizes[i]}`;
    }
    return `${(bytes / 1024 ** i).toFixed(1)}${separator}${sizes[i]}`;
};

const getImageOrientation = (file, callback) => {
    new Response(file).arrayBuffer().then((arrayBuffer) => {
        const view = new DataView(arrayBuffer);
        if (view.getUint16(0, false) !== 0xffd8) {
            return callback(-2);
        }
        const length = view.byteLength;
        let offset = 2;
        while (offset < length) {
            if (view.getUint16(offset + 2, false) <= 8) {
                return callback(-1);
            }
            const marker = view.getUint16(offset, false);
            offset += 2;
            if (marker === 0xffe1) {
                if (view.getUint32((offset += 2), false) !== 0x45786966) {
                    return callback(-1);
                }
                const little = view.getUint16((offset += 6), false) === 0x4949;
                offset += view.getUint32(offset + 4, little);
                const tags = view.getUint16(offset, little);
                offset += 2;
                for (let i = 0; i < tags; i++) {
                    if (view.getUint16(offset + i * 12, little) === 0x0112) {
                        return callback(view.getUint16(offset + i * 12 + 8, little));
                    }
                }
                // tslint:disable-next-line:no-bitwise
            }
            else if ((marker & 0xff00) !== 0xff00) {
                break;
            }
            else {
                offset += view.getUint16(offset, false);
            }
        }
        return callback(-1);
    });
};

const base64toBlob = (base64Data, contentType) => {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);
    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        const begin = sliceIndex * sliceSize;
        const end = Math.min(begin + sliceSize, bytesLength);
        const bytes = new Array(end - begin);
        for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
            bytes[i] = byteCharacters[offset].charCodeAt(0);
        }
        byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
};

const resetImageOrientation = (srcBase64, callback) => {
    const img = new Image();
    let srcOrientation = -1;
    const srcBlob = base64toBlob(srcBase64.substr(srcBase64.indexOf(',') + 1), null);
    getImageOrientation(srcBlob, (orientation) => {
        srcOrientation = orientation;
        // console.log('Orientation ' + srcOrientation);
        img.src = srcBase64;
    });
    img.onload = () => {
        const width = img.width;
        const height = img.height;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        // set proper canvas dimensions before transform & export
        if (4 < srcOrientation && srcOrientation < 9) {
            canvas.width = height;
            canvas.height = width;
        }
        else {
            canvas.width = width;
            canvas.height = height;
        }
        // transform context before drawing image
        switch (srcOrientation) {
            case 2:
                ctx.transform(-1, 0, 0, 1, width, 0);
                break;
            case 3:
                ctx.transform(-1, 0, 0, -1, width, height);
                break;
            case 4:
                ctx.transform(1, 0, 0, -1, 0, height);
                break;
            case 5:
                ctx.transform(0, 1, 1, 0, 0, 0);
                break;
            case 6:
                ctx.transform(0, 1, -1, 0, height, 0);
                break;
            case 7:
                ctx.transform(0, -1, -1, 0, height, width);
                break;
            case 8:
                ctx.transform(0, -1, 1, 0, 0, width);
                break;
            default:
                break;
        }
        // draw image
        ctx.drawImage(img, 0, 0);
        // export base64
        callback(canvas.toDataURL());
    };
};

const getFileNameExtension = (fileName) => fileName.split('.').pop().toLowerCase();

const hasFileNameOneOfExtensions = (fileName, extensions) => extensions.includes(getFileNameExtension(fileName));

const isFileNameImage = (fileName) => hasFileNameOneOfExtensions(fileName, [
    'jpg',
    'jpeg',
    'jif',
    'jfif',
    'png',
    'gif',
    'bmp',
]);

const isFileNameTextDocument = (fileName) => hasFileNameOneOfExtensions(fileName, [
    'doc',
    'docx',
    'pdf',
    'txt',
    'rtf',
    'odt',
]);

const isFileNamePdf = (fileName) => hasFileNameOneOfExtensions(fileName, ['pdf']);

const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
};

/**
 * @deprecated Use functions directly instead injecting via service.
 */
class FileService {
    /**
     * @deprecated Use the 'bytesToSize' function instead.
     */
    static bytesToSize(bytes, separator = ' ') {
        return bytesToSize(bytes, separator);
    }
    /**
     * @deprecated Use the 'getImageOrientation' function instead.
     */
    static getImageOrientation(file, callback) {
        return getImageOrientation(file, callback);
    }
    /**
     * @deprecated Use the 'resetImageOrientation' function instead.
     */
    static resetImageOrientation(srcBase64, callback) {
        return resetImageOrientation(srcBase64, callback);
    }
    /**
     * @deprecated Use the 'base64toBlob' function instead.
     */
    static base64toBlob(base64Data, contentType) {
        return base64toBlob(base64Data, contentType);
    }
    /**
     * @deprecated Use the 'getFileNameExtension' function instead.
     */
    fileNameExtension(fileName) {
        return getFileNameExtension(fileName);
    }
    /**
     * @deprecated Use the 'hasFileNameOneOfExtensions' function instead.
     */
    isFileNameType(fileName, extensions) {
        return hasFileNameOneOfExtensions(fileName, extensions);
    }
    /**
     * @deprecated Use the 'isFileNameImage' function instead.
     */
    isFileNameImage(fileName) {
        return isFileNameImage(fileName);
    }
    /**
     * @deprecated Use the 'isFileNameTextDocument' function instead.
     */
    isFileNameTextdoc(fileName) {
        return isFileNameTextDocument(fileName);
    }
    /**
     * @deprecated Use the 'isFileNamePdf' function instead.
     */
    isFileNamePdf(fileName) {
        return isFileNamePdf(fileName);
    }
    /**
     * @deprecated Use the 'fileToBase64' function instead.
     */
    getBase64(file) {
        return fileToBase64(file);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: FileService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: FileService, providedIn: FileModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: FileService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: FileModule,
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FileModule, FileService, base64toBlob, bytesToSize, fileToBase64, getFileNameExtension, getImageOrientation, hasFileNameOneOfExtensions, isFileNameImage, isFileNamePdf, isFileNameTextDocument, resetImageOrientation };
